import React, { useState } from 'react';
import PropTypes from 'prop-types';

const StepMediaSocial = ({ data, onChange }) => {
  const [logoPreview, setLogoPreview] = useState(null);
  const [imagePreviews, setImagePreviews] = useState([]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('Logo file size must be less than 2MB');
        return;
      }
      if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
        alert('Logo must be JPG or PNG format');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoPreview(event.target.result);
        onChange({ ...data, logo: file });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImagesUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (file.size > 2 * 1024 * 1024) {
        alert(`File ${file.name} must be less than 2MB`);
        return false;
      }
      if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
        alert(`File ${file.name} must be JPG or PNG format`);
        return false;
      }
      return true;
    });

    const currentImages = data.images || [];
    const totalImages = currentImages.length + validFiles.length;
    
    if (totalImages > 6) {
      alert('Maximum 6 images allowed');
      return;
    }

    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreviews(prev => [...prev, event.target.result]);
        onChange({ 
          ...data, 
          images: [...(data.images || []), file] 
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    const updatedImages = (data.images || []).filter((_, i) => i !== index);
    const updatedPreviews = imagePreviews.filter((_, i) => i !== index);
    setImagePreviews(updatedPreviews);
    onChange({ ...data, images: updatedImages });
  };

  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="step-card">
      <div className="form-group">
        <label>Business Logo</label>
        <div className="upload-container">
          <input
            type="file"
            id="logo-upload"
            accept="image/jpeg,image/jpg,image/png"
            onChange={handleLogoUpload}
            className="file-input"
          />
          <label htmlFor="logo-upload" className="upload-box">
            {logoPreview ? (
              <div className="preview-container">
                <img src={logoPreview} alt="Logo preview" className="logo-preview" />
                <div className="upload-overlay">
                  <div className="upload-icon">📷</div>
                  <span>Click to change</span>
                </div>
              </div>
            ) : (
              <div className="upload-placeholder">
                <div className="upload-icon">📷</div>
                <span>Upload Logo</span>
                <small>Max 2MB, JPG/PNG</small>
              </div>
            )}
          </label>
        </div>
      </div>

      <div className="form-group">
        <label>Business Images</label>
        <div className="upload-container">
          <input
            type="file"
            id="images-upload"
            accept="image/jpeg,image/jpg,image/png"
            multiple
            onChange={handleImagesUpload}
            className="file-input"
          />
          <label htmlFor="images-upload" className="upload-box">
            <div className="upload-placeholder">
              <div className="upload-icon">🖼️</div>
              <span>Upload Images</span>
              <small>Max 6 images, 2MB each, JPG/PNG</small>
            </div>
          </label>
        </div>
        
        {imagePreviews.length > 0 && (
          <div className="images-preview-grid">
            {imagePreviews.map((preview, index) => (
              <div key={index} className="image-preview-item">
                <img src={preview} alt={`Business image ${index + 1}`} />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="remove-image-btn"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="facebook">Facebook URL</label>
        <input
          type="url"
          id="facebook"
          value={data.facebook || ''}
          onChange={(e) => handleChange('facebook', e.target.value)}
          placeholder="https://www.facebook.com/yourbusiness"
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label htmlFor="instagram">Instagram URL</label>
        <input
          type="url"
          id="instagram"
          value={data.instagram || ''}
          onChange={(e) => handleChange('instagram', e.target.value)}
          placeholder="https://www.instagram.com/yourbusiness"
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label htmlFor="twitter">Twitter/X URL</label>
        <input
          type="url"
          id="twitter"
          value={data.twitter || ''}
          onChange={(e) => handleChange('twitter', e.target.value)}
          placeholder="https://www.twitter.com/yourbusiness"
          className="form-input"
        />
      </div>
    </div>
  );
};

StepMediaSocial.propTypes = {
  data: PropTypes.shape({
    logo: PropTypes.object,
    images: PropTypes.array,
    facebook: PropTypes.string,
    instagram: PropTypes.string,
    twitter: PropTypes.string
  }).isRequired,
  onChange: PropTypes.func.isRequired
};

export default StepMediaSocial;
